import { useEffect, useState } from 'react';
import { Check, Info } from 'lucide-react';
import { Button, Field, Input, Modal } from '../components/ui';
import { useUpdateMember } from '../api/queries';
import { useToast } from '../lib/toast';
import { isValidEmail, isValidPhone, isValidPlz } from '../lib/validate';
import { MITGLIEDSART_INFO, isUnder18 } from '../lib/format';
import type { CurrentUser, MemberDetail } from '../api/types';

type Tab = 'stamm' | 'verein';

interface Form {
  vorname: string;
  nachname: string;
  geburtsdatum: string;
  geschlecht: 'm' | 'w' | 'd';
  strasse: string;
  plz: string;
  ort: string;
  email: string;
  telefon: string;
  mitgliedsart: 'aktiv' | 'unterstuetzend' | 'jugend' | 'ehrenmitglied';
  eingetreten_am: string;
  ausgetreten_am: string;
}

function formFromMember(m: MemberDetail): Form {
  return {
    vorname: m.vorname ?? '',
    nachname: m.nachname ?? '',
    geburtsdatum: m.geburtsdatum ?? '',
    geschlecht: (m.geschlecht as Form['geschlecht']) ?? 'm',
    strasse: m.strasse ?? '',
    plz: m.plz ?? '',
    ort: m.ort ?? '',
    email: m.email ?? '',
    telefon: m.telefon ?? '',
    mitgliedsart: (m.mitgliedsart as Form['mitgliedsart']) ?? 'aktiv',
    eingetreten_am: m.eingetretenAm ?? '',
    ausgetreten_am: m.ausgetretenAm ?? '',
  };
}

function diff(initial: Form, current: Form): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const k of Object.keys(current) as (keyof Form)[]) {
    if (initial[k] !== current[k]) out[k] = current[k];
  }
  return out;
}

export function EditMemberModal({
  open, onClose, member, initialTab = 'stamm',
}: {
  open: boolean;
  onClose: () => void;
  member: MemberDetail;
  user: CurrentUser;
  initialTab?: Tab;
}) {
  const [tab, setTab] = useState<Tab>(initialTab);
  const [form, setForm] = useState<Form>(formFromMember(member));
  const [initial, setInitial] = useState<Form>(formFromMember(member));
  const update = useUpdateMember(member.id);
  const toast = useToast();

  useEffect(() => {
    if (open) {
      const f = formFromMember(member);
      setForm(f);
      setInitial(f);
      setTab(initialTab);
    }
  }, [open, member, initialTab]);

  const upd = <K extends keyof Form>(k: K, v: Form[K]) => setForm((f) => ({ ...f, [k]: v }));

  const emailErr = form.email && !isValidEmail(form.email) ? 'Bitte eine gültige E-Mail eingeben.' : null;
  const phoneErr = form.telefon && !isValidPhone(form.telefon) ? 'Telefonnummer wirkt ungültig.' : null;
  const plzErr = form.plz && !isValidPlz(form.plz) ? 'PLZ muss vierstellig sein.' : null;
  const canSave = !!form.vorname.trim() && !!form.nachname.trim()
    && !emailErr && !phoneErr && !plzErr;

  const forceJugend = isUnder18(form.geburtsdatum);
  const effectiveMitgliedsart = forceJugend ? 'jugend' : form.mitgliedsart;

  const submit = () => {
    if (!canSave) return;
    const normalized: Form = { ...form, mitgliedsart: effectiveMitgliedsart };
    const patch = diff(initial, normalized);
    if (Object.keys(patch).length === 0) { onClose(); return; }
    update.mutate(patch, {
      onSuccess: () => {
        toast.push('Mitglied gespeichert');
        onClose();
      },
      onError: () => toast.push('Konnte nicht speichern', 'warn'),
    });
  };

  const tabs: [Tab, string][] = [
    ['stamm', 'Stammdaten'],
    ['verein', 'Verein'],
  ];

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${member.vorname} ${member.nachname} bearbeiten`}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
          <Button variant="primary" icon={<Check size={16} />} onClick={submit} disabled={update.isPending || !canSave}>
            Speichern
          </Button>
        </>
      }
    >
      <div className="inspector-tabs" style={{ marginBottom: 16 }}>
        {tabs.map(([id, label]) => (
          <button
            key={id}
            type="button"
            className={`inspector-tab ${tab === id ? 'active' : ''}`}
            onClick={() => setTab(id)}
          >
            {label}
          </button>
        ))}
      </div>

      {tab === 'stamm' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="grid-2">
            <Field label="Vorname" required><Input value={form.vorname} onChange={(e) => upd('vorname', e.target.value)} /></Field>
            <Field label="Nachname" required><Input value={form.nachname} onChange={(e) => upd('nachname', e.target.value)} /></Field>
          </div>
          <div className="grid-2">
            <Field label="Geburtsdatum"><Input type="date" value={form.geburtsdatum} onChange={(e) => upd('geburtsdatum', e.target.value)} /></Field>
            <Field label="Geschlecht">
              <div className="segmented" style={{ width: '100%' }}>
                {(['m', 'w', 'd'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={form.geschlecht === v ? 'active' : ''}
                    onClick={() => upd('geschlecht', v)}
                    style={{ flex: 1, justifyContent: 'center' }}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </Field>
          </div>
          <Field label="Straße & Hausnummer"><Input value={form.strasse} onChange={(e) => upd('strasse', e.target.value)} /></Field>
          <div className="grid-2">
            <Field label="PLZ" error={plzErr}><Input value={form.plz} onChange={(e) => upd('plz', e.target.value)} maxLength={4} inputMode="numeric" /></Field>
            <Field label="Ort"><Input value={form.ort} onChange={(e) => upd('ort', e.target.value)} /></Field>
          </div>
          <div className="grid-2">
            <Field label="Telefon" error={phoneErr}><Input className="t-mono" value={form.telefon} onChange={(e) => upd('telefon', e.target.value)} /></Field>
            <Field label="E-Mail" error={emailErr}><Input type="email" className="t-mono" value={form.email} onChange={(e) => upd('email', e.target.value)} /></Field>
          </div>
        </div>
      )}

      {tab === 'verein' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label="Mitgliedsart" hint={forceJugend ? 'Automatisch Jugendmitglied (unter 18 Jahre)' : undefined}>
            <select
              className="input"
              value={effectiveMitgliedsart}
              onChange={(e) => upd('mitgliedsart', e.target.value as Form['mitgliedsart'])}
              disabled={forceJugend}
            >
              <option value="aktiv">Aktives Mitglied</option>
              <option value="unterstuetzend">Unterstützendes Mitglied</option>
              <option value="jugend">Jugendmitglied</option>
              <option value="ehrenmitglied">Ehrenmitglied</option>
            </select>
          </Field>
          <div className="muted-card" style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13 }}>
            <Info size={14} className="text-accent" style={{ flexShrink: 0, marginTop: 2 }} />
            <span>{MITGLIEDSART_INFO[effectiveMitgliedsart]}</span>
          </div>
          <div className="muted-card" style={{ fontSize: 12.5, color: 'var(--text-tertiary)' }}>
            <strong style={{ color: 'var(--text-secondary)' }}>Funktionen:</strong> werden pro Sektion im Inspector direkt vergeben (z. B. „Trainer" in Fußball, „Obmann Stv." im Vorstand).
          </div>
          <div className="grid-2">
            <Field label="Eintrittsdatum" hint="Optional — nur ausfüllen, wenn bekannt."><Input type="date" value={form.eingetreten_am} onChange={(e) => upd('eingetreten_am', e.target.value)} /></Field>
            <Field label="Austrittsdatum (optional)"><Input type="date" value={form.ausgetreten_am} onChange={(e) => upd('ausgetreten_am', e.target.value)} /></Field>
          </div>
        </div>
      )}
    </Modal>
  );
}
